"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = getProductConfiguration;
var _functions = require("../utils/functions");
var _domainUtils = require("../utils/domain-utils");
var _appConfig = require("../config/app-config");
var _constants = require("../utils/constants");
/* eslint-disable max-len, no-undefined, complexity */

class ProductConfigurationFormatter {
  constructor({
    data,
    environment,
    content,
    instance
  }) {
    this.data = data;
    this.content = content;
    this.instance = instance;
    this.environment = environment;
    this.market = environment.market || _appConfig.DEFAULT_VALUES.market;
    this.currency = environment.currency || _appConfig.DEFAULT_VALUES.currency;
    this.featureFlags = content.featureFlags || [];

    /**
     * Maps display types to handler methods to avoid switch statements
     * in the processing loop
     */
    this.DISPLAY_HANDLERS = {
      termSelector: this.getTermSelector.bind(this),
      quantitySelector: this.getQuantitySelector.bind(this),
      pillSelector: this.getPillSelector.bind(this),
      toggle: this.getToggle.bind(this),
      featureBanner: this.getFeatureBanner.bind(this)
    };
  }
  getRowTitle(displayArea, productData, productAppliedTitles) {
    const titleKey = `${displayArea}RowTitle`;
    if (!productAppliedTitles[titleKey] && productData.content[titleKey]) {
      productAppliedTitles[titleKey] = true;
      return productData.content[titleKey];
    }
    return null;
  }
  getTermOptionPriceDescription(isDomain, priceData, priceDescription, termTotalPrice) {
    const {
      salepriceformatted,
      listpriceformatted
    } = priceData;
    if (isDomain) {
      // For domains: use listpriceformatted as total price (no /yr suffix) if available and different from sale price
      return listpriceformatted && listpriceformatted !== salepriceformatted ? listpriceformatted : '';
    }
    // For non-domains: use existing priceDescription logic
    return priceDescription && priceDescription.includes('{totalPrice}') ? priceDescription.replace('{totalPrice}', termTotalPrice).trim() : '';
  }
  getTermSavingsAmountTag(savings) {
    return savings && savings.length > 0 ? this.content?.saveAmount?.replace('{value}', savings) : '';
  }
  getTermSavingsTag(percentagesavings) {
    return percentagesavings && percentagesavings !== 0 ? this.content?.savePercentage?.replace('{value}', percentagesavings) : '';
  }
  getTermLengthDisplay(content, enumVal, displayEnumVal, termTypeText) {
    return content[enumVal]?.title?.includes('{termType}') ? `${displayEnumVal} ${termTypeText}` : content[enumVal]?.title || `${displayEnumVal} ${termTypeText}`;
  }
  getTermSelector(product, productData) {
    const {
      term: {
        content,
        attributes
      },
      prices = {}
    } = productData;
    const {
      enum: enums,
      display
    } = attributes;

    // Check if this is a domain product
    const isDomain = (0, _domainUtils.isProductDomain)(this.instance, product);
    const {
      numberOfTerms: defaultTerm,
      termType
    } = (0, _functions.getProductTerm)(this.instance, product);
    const useNewTermSelector = this.featureFlags.includes(_constants.FEATURE_FLAGS.USE_NEW_TERM_SELECTOR);
    const {
      priceDescription
    } = content.shared;
    const termPath = `${product}/properties/term`;
    const termID = `id-${termPath}`;
    const termIndex = enums.indexOf(parseInt(defaultTerm, 10));
    const itemOptions = enums.map(enumVal => {
      const optionId = `${termID}.numberOfTerms.${enumVal}`;
      const priceData = prices[enumVal] || {};
      const {
        sale,
        list,
        percentagesavings,
        salepriceformatted,
        listpriceformatted
      } = priceData;
      const termTotalPrice = sale || sale === 0 ? (0, _functions.formatMicrounits)(sale * enumVal, this.market, this.currency) : undefined;
      const termSavingsAmount = sale && list && list > sale ? (0, _functions.formatMicrounits)(list * enumVal - sale * enumVal, this.market, this.currency) : undefined;
      const termListPrice = list > sale ? listpriceformatted : undefined; // added termListPrice
      const optionPriceDescription = this.getTermOptionPriceDescription(isDomain, priceData, priceDescription, termTotalPrice);

      // For domains, convert months to years for display
      const displayTermType = isDomain ? 'YEAR' : termType;
      const displayEnumVal = isDomain ? enumVal / 12 : enumVal;

      // For domains, we need to use the displayEnumVal for term text
      const termTypeText = (0, _functions.getTermTypeText)(displayTermType, this.content, displayEnumVal);
      return {
        id: optionId,
        _name: optionId.replace('.', '_'),
        rightSavingsTag: useNewTermSelector ? this.getTermSavingsTag(percentagesavings) : undefined,
        savingsTag: useNewTermSelector ? this.getTermSavingsAmountTag(termSavingsAmount) : this.getTermSavingsTag(percentagesavings),
        tagDesign: useNewTermSelector ? 'highlight' : 'warning',
        termLength: this.getTermLengthDisplay(content, enumVal, displayEnumVal, termTypeText),
        termPrice: isDomain ? salepriceformatted : `${salepriceformatted}${(0, _functions.getTermPriceText)(displayTermType, this.content)}`,
        termTotalPrice: useNewTermSelector ? termListPrice : optionPriceDescription,
        useStrikeThru: useNewTermSelector,
        numberOfTerm: enumVal,
        isDomain
      };
    });
    const termSelector = {
      ...content.shared,
      instancePath: termPath,
      id: termID,
      layouts: display,
      defaultSelected: termIndex,
      items: [{
        ariaLabel: display || 'select-term-dropdown',
        options: itemOptions
      }]
    };
    return {
      termSelector,
      displayArea: 'properties'
    };
  }
  getQuantitySelector(product, productData, {
    itemData,
    itemName
  }) {
    // Handle main product quantity selector (pkgQty)
    if (productData?.pkgQty) {
      const quantityPath = `${product}/properties/quantity`;
      const {
        pkgQty: {
          content,
          attributes: {
            display,
            displayDisable
          }
        }
      } = productData;
      const quantitySelector = {
        ...content.shared,
        instancePath: quantityPath,
        id: `id-${quantityPath}`,
        layouts: display,
        displayDisable
      };
      return {
        quantitySelector,
        displayArea: 'properties'
      };
    }

    // Handle addon quantity selector
    if (itemData?.attributes?.display === 'quantitySelector') {
      const {
        content,
        attributes,
        prices = {}
      } = itemData;
      const {
        display,
        displayArea,
        displayDisable,
        default: defaultSelection,
        min,
        max,
        defaultQty
      } = attributes;
      const quantityPath = `${product}/${displayArea}/${itemName}`;

      // Get the initial quantity from the defaultQty
      const initialValue = defaultQty ? parseInt(defaultQty, 10) : 0;

      // Build help message with pricing data (following same pattern as getToggle)
      const {
        termType
      } = productData.term.attributes;
      const priceData = prices[defaultSelection] || {};
      const {
        salepriceformatted
      } = priceData;
      let helpMessage = null;
      if (salepriceformatted) {
        const termPriceText = (0, _functions.getTermPriceText)(termType, this.content);
        helpMessage = `${salepriceformatted}${termPriceText} ${this.content.perSite}`;
      }
      const quantitySelector = {
        ...content.shared,
        instancePath: quantityPath,
        id: `id-${quantityPath}`,
        layouts: display,
        displayDisable,
        initialValue,
        helpMessage,
        isAddon: true,
        // Include min/max if they exist in attributes (following same pattern as pkgQty)
        ...(min !== undefined && {
          min
        }),
        ...(max !== undefined && {
          max
        })
      };
      return {
        quantitySelector,
        displayArea: 'properties'
      };
    }
    return null;
  }

  // eslint-disable-next-line max-statements
  getToggle(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      content,
      prices = {},
      attributes: {
        display,
        displayArea,
        displayDisable,
        enum: enums,
        fixedNumberOfTerms,
        productType
      }
    } = itemData;
    const isTopLevel = product === itemName;
    const isBundleToggle = (0, _functions.isBundleProduct)(this.instance, product);
    const {
      numberOfTerms: productTermLength,
      termType
    } = (0, _functions.getProductTerm)(this.instance, product);
    const togglePath = isTopLevel ? `${product}/properties` : `${product}/${displayArea}/${itemName}`;
    const {
      priceOverride,
      priceDescription,
      perMonthOverride,
      rowTitle
    } = content.shared;
    const priceData = prices[productTermLength] ?? prices[enums[0]] ?? {};
    const {
      salepriceformatted,
      sale,
      percentagesavings,
      percentagesavingsformatted
    } = priceData;
    const quantity = (0, _functions.getProductQuantity)(this.instance, product);
    const termLength = fixedNumberOfTerms || productTermLength;
    const isPillToggle = enums.length > 1;

    // For multi-domain scenarios, the price is always calculated for Default Term
    const effectiveTermLength = (0, _domainUtils.isMultiDomain)(this.environment) && (0, _domainUtils.isProductDomain)(this.instance, product) ? 1 : termLength;
    const togglePrice = (0, _functions.calculateTotalPrice)(sale, effectiveTermLength, quantity, this.market, this.currency);
    const currentSelection = (0, _functions.getSelectedOptionValue)(this.instance, togglePath);
    let items = [];
    if (isPillToggle) {
      const defaultEnum = enums[0];
      const defaultEnumContent = {
        ...content[defaultEnum]
      };
      delete defaultEnumContent.list;
      const pillToggleItem = {
        ...content.shared,
        ...defaultEnumContent,
        selection: currentSelection,
        title: content.shared.title,
        productTagLabels: (0, _functions.getProductTagLabels)(defaultEnumContent.tags, defaultEnumContent.tagLabel),
        instancePath: togglePath,
        id: defaultEnum,
        _name: defaultEnum,
        tag: this.content.startingAt,
        price: (0, _functions.getPriceDisplay)(priceOverride, salepriceformatted, termType, this.content, perMonthOverride),
        priceDescription: (0, _functions.getPriceDescription)(togglePrice, priceDescription),
        displayDisable,
        isPillToggle: true,
        options: enums.map(enumVal => {
          const optionData = prices[enumVal] || prices[productTermLength] || {};
          const {
            salepriceformatted: optionPriceFormatted,
            sale: optionSale
          } = optionData;
          const individualOptionPrice = (0, _functions.calculateTotalPrice)(optionSale, termLength, quantity, this.market, this.currency);
          return {
            ...content[enumVal],
            productTagLabels: (0, _functions.getProductTagLabels)(content[enumVal].tags, content[enumVal].tagLabel),
            id: enumVal,
            _name: enumVal,
            value: enumVal,
            tag: (0, _functions.getSavingsTag)(optionData.percentagesavings, this.content.savePercentage),
            price: (0, _functions.getPriceDisplay)(content[enumVal].priceOverride, salepriceformatted, termType, this.content, perMonthOverride, optionPriceFormatted),
            priceDescription: (0, _functions.getPriceDescription)(individualOptionPrice, content[enumVal].priceDescription, priceDescription),
            ...(content[enumVal]?.list && {
              list: content[enumVal].list
            })
          };
        })
      };
      items = [pillToggleItem];
    } else {
      items = enums.map(enumVal => {
        const enumPriceDescription = content[enumVal].priceDescription || priceDescription;
        const {
          tagLabel,
          tags
        } = content[enumVal];
        const productTagLabels = (0, _functions.getProductTagLabels)(tags, tagLabel);
        let title = content[enumVal].title || content.shared.title;
        if (isBundleToggle) {
          title = title.replace('{savePercentage}', percentagesavingsformatted);
        }
        return {
          ...content.shared,
          ...content[enumVal],
          title,
          selection: currentSelection,
          productTagLabels,
          instancePath: togglePath,
          id: enumVal,
          _name: enumVal,
          tag: (0, _functions.getSavingsTag)(percentagesavings, this.content.savePercentage),
          price: (0, _functions.getPriceDisplay)(priceOverride, salepriceformatted, termType, this.content, perMonthOverride),
          priceDescription: (0, _functions.getPriceDescription)(togglePrice, enumPriceDescription),
          displayDisable
        };
      });
    }
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const attachmentSelector = {
      productType,
      layouts: display,
      rowTitle: selectorRowTitle,
      items,
      isBundleToggle
    };
    return {
      attachmentSelector,
      displayArea
    };
  }
  getPillSelector(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      content,
      prices = {},
      attributes: {
        display,
        displayDisable,
        displayArea,
        split,
        enum: enums,
        fixedNumberOfTerms,
        productType
      }
    } = itemData;
    const isTopLevel = product === itemName;
    const {
      numberOfTerms: productTermLength,
      termType
    } = (0, _functions.getProductTerm)(this.instance, product);
    const contentPath = isTopLevel ? `${product}/properties` : `${product}/${displayArea}/${itemName}`;
    const {
      rowTitle,
      priceDescription,
      perMonthOverride
    } = content.shared;
    const priceData = prices[productTermLength] ?? prices[enums[0]] ?? {};
    const {
      salepriceformatted
    } = priceData;

    // Check if PreCheck is in Domains flow (has any domain products in the instance)
    const isDomainsFlow = (0, _domainUtils.hasDomainsInFlow)(this.instance);
    const quantity = (0, _functions.getProductQuantity)(this.instance, product);
    const termLength = fixedNumberOfTerms || productTermLength;
    const shouldShowPricing = displayArea === 'addons';
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const options = enums.map(enumVal => {
      const {
        title,
        description,
        tagLabel,
        list,
        tags
      } = content[enumVal];
      const productTagLabels = (0, _functions.getProductTagLabels)(tags, tagLabel);
      const optionPath = isTopLevel ? `${contentPath}` : `${contentPath}/${enumVal}`;
      const baseOption = {
        title: title || 'Product Name',
        productTagLabels,
        description,
        id: enumVal,
        instancePath: optionPath,
        ...(list && {
          list
        })
      };

      // Add pricing information only when displayArea is 'addons'
      if (shouldShowPricing && enumVal !== 'none') {
        const optionData = prices[enumVal] || prices[productTermLength] || {};
        const {
          salepriceformatted: optionPriceFormatted,
          sale: optionSale,
          percentagesavings,
          list: listPrice,
          listpriceformatted
        } = optionData;
        const individualOptionPrice = (0, _functions.calculateTotalPrice)(optionSale, termLength, quantity, this.market, this.currency);

        // For domains flow, use strike-through pricing when list price is higher than sale price
        const termListPrice = listPrice > optionSale ? listpriceformatted : undefined;
        const shouldUseStrikeThru = !!(isDomainsFlow && termListPrice);
        baseOption.tag = (0, _functions.getSavingsTag)(percentagesavings, this.content.savePercentage);
        baseOption.price = (0, _functions.getPriceDisplay)(content[enumVal].priceOverride, salepriceformatted, termType, this.content, perMonthOverride, optionPriceFormatted);
        baseOption.priceDescription = shouldUseStrikeThru ? termListPrice : (0, _functions.getPriceDescription)(individualOptionPrice, content[enumVal].priceDescription, priceDescription);
        baseOption.useStrikeThru = shouldUseStrikeThru;
      }
      return baseOption;
    });
    const {
      tagLabel,
      tags,
      ...sharedContent
    } = content.shared;
    const sharedTagLabels = (0, _functions.getProductTagLabels)(tags, tagLabel);
    const optionSelector = {
      ...sharedContent,
      ...itemData,
      productType,
      sharedTagLabels,
      rowTitle: selectorRowTitle,
      instancePath: contentPath,
      id: `id-${contentPath}`,
      layouts: display,
      displayDisable,
      items: [{
        selectedOption: (0, _functions.getSelectedOptionValue)(this.instance, contentPath),
        split,
        options
      }]
    };
    return {
      optionSelector,
      displayArea
    };
  }
  getFeatureBanner(product, productData, {
    itemData,
    itemName
  }, productAppliedTitles) {
    const {
      content,
      attributes: {
        display,
        displayArea,
        productType
      }
    } = itemData;
    const addonPath = `${product}/${displayArea}/${itemName}`;
    const {
      rowTitle
    } = content.shared;
    const selectorRowTitle = this.getRowTitle(displayArea, productData, productAppliedTitles) || rowTitle;
    const featureBanner = {
      ...content.shared,
      productType,
      rowTitle: selectorRowTitle,
      instancePath: addonPath,
      id: `id-${addonPath}`,
      layouts: display
    };
    return {
      featureBanner,
      displayArea
    };
  }
  processItem(product, productData, item, productAppliedTitles) {
    const display = item.itemData.attributes.display;
    const handler = this.DISPLAY_HANDLERS[display];
    if (handler) {
      return handler(product, productData, item, productAppliedTitles);
    }
    return {
      result: null,
      displayArea: null
    };
  }
  generate() {
    // Create flattened structure with category arrays
    const productConfiguration = {
      properties: [],
      offers: [],
      includes: [],
      addons: [],
      banners: []
    };

    // Track added items by itemName for multi-domain scenarios
    const addedMultiDomainItems = new Set();
    Object.entries(this.data).forEach(([product, productData]) => {
      const productAppliedTitles = {
        includesRowTitle: false,
        addonsRowTitle: false,
        offersRowTitle: false,
        bannersRowTitle: false
      };
      Object.entries(productData).forEach(([groupName, groupData]) => {
        Object.entries(groupData).forEach(([contentName, contentData]) => {
          let item;
          if (contentData?.display) {
            item = {
              itemName: groupName,
              itemData: groupData
            };
          } else if (contentData?.attributes?.display) {
            item = {
              itemName: contentName,
              itemData: contentData
            };
          } else {
            return;
          }
          const {
            displayArea,
            ...result
          } = this.processItem(product, productData, item, productAppliedTitles);
          if (result) {
            if ((0, _domainUtils.isMultiDomain)(this.environment) && (0, _domainUtils.isProductDomain)(this.instance, product)) {
              if (result.termSelector) {
                return; // Exclude term selector for multi-domain scenarios
              }

              // Skip duplicate items for multi-domain scenarios
              const itemKey = `${displayArea}-${item.itemName}`;
              if (addedMultiDomainItems.has(itemKey)) {
                return;
              }
              addedMultiDomainItems.add(itemKey);
            }

            // Push item with productId to the appropriate category array
            productConfiguration[displayArea].push({
              item: result,
              productId: product
            });
          }
        });
      });
    });
    return productConfiguration;
  }
}
function getProductConfiguration(responseData = {}) {
  const formatter = new ProductConfigurationFormatter(responseData);
  return formatter.generate();
}